import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { TagIcon } from "@heroicons/react/24/outline";
import api from "../../../../utils/axios";
import ProductSearchBar from "../components/ProductSearchBar";
import ProductStatusBadge from "../components/ProductStatusBadge";
import ProductTablePagination from "../components/ProductTablePagination";
import ProductTableAction from "../components/ProductTableAction";

const limit = 5;

export default function ProductTagsTable() {
    const navigate = useNavigate();
    const [tags, setTags] = useState([]);
    const [search, setSearch] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [total, setTotal] = useState(0);
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        const timeoutId = setTimeout(() => {
        setDebouncedSearch(search);
        setPage(1);
        }, 300);

        return () => clearTimeout(timeoutId);
    }, [search]);

    useEffect(() => {
        const fetchTags = async () => {
        try {
            setIsLoading(true);

            const response = await api.get("/tags/allProductTagsTable", {
            params: {
                page,
                limit,
                search: debouncedSearch,
            },
            });

            setTags(response.data.data || []);
            setTotal(response.data.total || 0);
            setTotalPages(response.data.totalPages || 1);
        } catch (error) {
            console.log("Error al obtener etiquetas:", error);
        } finally {
            setIsLoading(false);
        }
        };

        fetchTags();
    }, [page, debouncedSearch]);

    const goToTag = (tagId) => {
        navigate(`/productos/new-table/tags/${tagId}`);
    };

    const editTag = (tagId) => {
        navigate(`/tags/edit/${tagId}`);
    };

    return (
        <div className="main-container">
        <div className="tw-flex tw-flex-col tw-gap-4 tw-px-3 tw-py-4">
            <div className="tw-flex tw-items-center tw-justify-between tw-gap-4">
            <h1 className="tw-m-0 tw-text-xl tw-font-bold tw-text-slate-950">
                Etiquetas
            </h1>

            <button
                type="button"
                onClick={() => navigate("/tags/add")}
                className="tw-rounded-lg tw-bg-black tw-px-4 tw-py-2 tw-text-sm tw-font-semibold tw-text-white tw-transition hover:tw-bg-slate-800"
            >
                + Nuevo producto
            </button>
            </div>

            <ProductSearchBar value={search} onChange={setSearch} />

            <div className="tw-overflow-hidden tw-rounded-xl tw-border tw-border-slate-200 tw-bg-white">
            <div className="tw-overflow-x-auto">
                <table className="tw-w-full tw-border-collapse tw-text-sm">
                <thead>
                    <tr className="tw-border-b tw-border-slate-200 tw-bg-white tw-text-left tw-text-xs tw-font-semibold tw-text-slate-900">
                    <th className="tw-w-12 tw-px-5 tw-py-3">
                        <TagIcon className="tw-h-4 tw-w-4 tw-text-slate-500" />
                    </th>
                    <th className="tw-px-5 tw-py-3">Etiqueta</th>
                    <th className="tw-px-5 tw-py-3">Estado</th>
                    <th className="tw-px-5 tw-py-3">Proveedor</th>
                    <th className="tw-px-5 tw-py-3 tw-text-center">Productos</th>
                    <th className="tw-px-5 tw-py-3"></th>
                    </tr>
                </thead>

                <tbody>
                    {isLoading ? (
                    <tr>
                        <td colSpan={6} className="tw-px-5 tw-py-8 tw-text-center tw-text-slate-500">
                        Cargando etiquetas...
                        </td>
                    </tr>
                    ) : tags.length ? (
                    tags.map((tag) => (
                        <tr
                        key={tag._id}
                        onClick={() => goToTag(tag._id)}
                        className="tw-cursor-pointer tw-border-b tw-border-slate-200 tw-transition last:tw-border-b-0 hover:tw-bg-slate-50"
                        >
                        <td className="tw-px-5 tw-py-3">
                            <span className="tw-inline-flex tw-h-8 tw-w-8 tw-items-center tw-justify-center tw-rounded-lg tw-bg-cyan-50 tw-text-cyan-700">
                            <TagIcon className="tw-h-4 tw-w-4" />
                            </span>
                        </td>
                        <td className="tw-px-5 tw-py-3 tw-font-semibold tw-text-slate-950">
                            {tag.name}
                        </td>
                        <td className="tw-px-5 tw-py-3">
                            <ProductStatusBadge status={tag.status} />
                        </td>
                        <td className="tw-px-5 tw-py-3 tw-text-slate-600">
                            {tag.provider?.name || "-"}
                        </td>
                        <td className="tw-px-5 tw-py-3 tw-text-center">
                            <span className="tw-inline-flex tw-min-w-7 tw-items-center tw-justify-center tw-rounded-lg tw-bg-slate-100 tw-px-2 tw-py-1 tw-font-semibold tw-text-slate-700">
                            {tag.productCount || 0}
                            </span>
                        </td>
                        <td className="tw-px-5 tw-py-3">
                            <ProductTableAction
                            onEdit={() => editTag(tag._id)}
                            onOpen={() => goToTag(tag._id)}
                            />
                        </td>
                        </tr>
                    ))
                    ) : (
                    <tr>
                        <td colSpan={6} className="tw-px-5 tw-py-8 tw-text-center tw-text-slate-500">
                        No se encontraron etiquetas
                        </td>
                    </tr>
                    )}
                </tbody>
                </table>
            </div>

            <ProductTablePagination
                page={page}
                totalPages={totalPages}
                total={total}
                limit={limit}
                onPageChange={setPage}
            />
            </div>
        </div>
        </div>
    );
}